import { useCallback, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import localStorageService from 'services/local-storage-service';
import { useAppDispatch } from 'store';
import { setAuthTokenAction } from 'store/actions/auth-actions';
import { setMenuStatusAction } from 'store/actions/menu-actions';
import IToken from 'types/auth/IToken';
import { MfaCard, MfaPageContainer, MfaRightColumn } from '../mfa-styles';
import { AuthLeftContainer } from '../styles';
import MfaBackupCodeAcceptedView from './MfaBackupCodeAcceptedView';
import MfaBackupCodeView from './MfaBackupCodeView';
import MfaTotpView from './MfaTotpView';
import useMfaChallenge from './useMfaChallenge';

/**
 * `totp` and `backup-code` are two ways to answer one challenge, not two flows — the user toggles
 * freely between them and whichever succeeds spends the same challenge row. `accepted` is reached
 * only from `backup-code`, and only in one direction.
 */
type MfaChallengeStep = 'totp' | 'backup-code' | 'accepted';

/**
 * The returning-user challenge: one screen between the login form and the dashboard for a user who
 * is already enrolled and is asked for a code on every login.
 *
 * The mfaToken arrives in `location.state` and lives only in memory — it is not a session token, it
 * expires in 5 minutes, and every protected endpoint rejects it. A refresh empties `location.state`,
 * which is why the guard below sends the user back to login rather than trying to recover: the next
 * login issues a fresh token. That 5-minute clock covers the backup-code detour too, so a user who
 * spends six minutes hunting for their printout lands back on login with SESSION_EXPIRED.
 *
 * Unlike enrollment there is nothing to fetch on mount — the login response carried everything this
 * screen needs, and the single call happens only when the user submits.
 */
const MfaChallenge = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [step, setStep] = useState<MfaChallengeStep>('totp');
  /**
   * The session won by a backup code — already persisted, but not yet activated, so the user sees
   * how many codes they have left before the router swaps out from under this screen. Non-null
   * exactly when `step === 'accepted'`, and its `remainingBackupCodes` is a number by the same
   * construction.
   */
  const [acceptedSession, setAcceptedSession] = useState<IToken>(null);
  const mfaToken = (location.state as { mfaToken?: string })?.mfaToken;

  const goToLoginWithMessage = useCallback(
    (messageKey: string) => {
      navigate('/auth/login', { replace: true, state: { mfaErrorKey: messageKey } });
    },
    [navigate],
  );

  const { submitting, failure, retrySeconds, verify, verifyBackupCode, clearFailure } = useMfaChallenge(
    mfaToken,
    goToLoginWithMessage,
  );

  /**
   * Writes the session where `services/startup/load-auth` will find it on the next bootstrap, but
   * does not sign the user in yet.
   *
   * Split from `activateSession` only for the backup-code path, which holds the user on a notice for
   * a moment before landing them. A code is single-use and cannot be reissued by anyone, so a stray
   * refresh on that notice must not cost the user the session they just spent one to get: persisting
   * first means a refresh recovers it and merely skips the notice, instead of returning them to
   * login needing a second code they may not have.
   */
  const persistSession = useCallback((session: IToken) => {
    localStorageService.setItem({ authToken: session.token });
  }, []);

  // Signing in is the last thing that happens: it flips authStateSelector, which swaps Router from
  // AuthRouter to AppRouter. AppRouter's catch-all then resolves /auth/mfa-challenge to /dashboard,
  // so no explicit navigate is needed — this mirrors what an ordinary login does.
  const activateSession = useCallback(
    (session: IToken) => {
      dispatch(setAuthTokenAction(session.token));
      dispatch(setMenuStatusAction('Dashboard'));
    },
    [dispatch],
  );

  const onSubmitTotp = useCallback(
    async (code: string) => {
      const session = await verify(code);
      if (session) {
        persistSession(session);
        activateSession(session);
      }
    },
    [verify, persistSession, activateSession],
  );

  const onSubmitBackupCode = useCallback(
    async (code: string): Promise<boolean> => {
      const session = await verifyBackupCode(code);
      if (!session) {
        return false;
      }
      persistSession(session);
      // 0 is a real count — the case with its own copy — so this is an explicit null check, never a
      // truthiness one. A missing count would mean the contract changed under us; rather than show a
      // notice with nothing to say, land the user as an ordinary verify would.
      if (session.remainingBackupCodes === null || session.remainingBackupCodes === undefined) {
        activateSession(session);
        return true;
      }
      setAcceptedSession(session);
      setStep('accepted');
      return true;
    },
    [verifyBackupCode, persistSession, activateSession],
  );

  const onContinueAfterBackupCode = useCallback(
    () => activateSession(acceptedSession),
    [activateSession, acceptedSession],
  );

  const onBackToLogin = useCallback(() => navigate('/auth/login', { replace: true }), [navigate]);

  // Switching inputs clears the error but not the countdown: the backoff is shared with the
  // authenticator attempts on the server, so a stale error would mislead while a live countdown
  // still tells the truth.
  const onUseBackupCode = useCallback(() => {
    clearFailure();
    setStep('backup-code');
  }, [clearFailure]);

  const onUseAuthenticator = useCallback(() => {
    clearFailure();
    setStep('totp');
  }, [clearFailure]);

  if (!mfaToken) {
    return <Navigate to="/auth/login" replace />;
  }

  return (
    <MfaPageContainer>
      <AuthLeftContainer />
      <MfaRightColumn>
        <MfaCard>
          {step === 'totp' && (
            <MfaTotpView
              submitting={submitting}
              failure={failure}
              retrySeconds={retrySeconds}
              onSubmit={onSubmitTotp}
              onBackToLogin={onBackToLogin}
              onUseBackupCode={onUseBackupCode}
              onCodeEdited={clearFailure}
            />
          )}
          {step === 'backup-code' && (
            <MfaBackupCodeView
              submitting={submitting}
              failure={failure}
              retrySeconds={retrySeconds}
              onSubmit={onSubmitBackupCode}
              onBackToLogin={onBackToLogin}
              onUseAuthenticator={onUseAuthenticator}
              onCodeEdited={clearFailure}
            />
          )}
          {step === 'accepted' && (
            <MfaBackupCodeAcceptedView
              remaining={acceptedSession.remainingBackupCodes}
              onContinue={onContinueAfterBackupCode}
            />
          )}
        </MfaCard>
      </MfaRightColumn>
    </MfaPageContainer>
  );
};

export default MfaChallenge;
